import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, switchMap, throwError } from 'rxjs';

import { IToasterService, TOASTER_SERVICE } from '@shared/services';

import {
  ERROR_EXCEPTION_CODES,
  HTTP_STATUS,
  HttpStatusCode,
  INVALID_API_TOKEN_ERROR_MESSAGE,
  MAINTENANCE_ERROR_STATUSES,
  SERVER_ERROR_MESSAGES
} from '../constants/error.constants';
import { ApiHealthCheckService } from '../services/api-health-check.service';
import {
  forEachApiErrorBody,
  hasAnyApiErrorInPayload,
  hasGraphQLMutationValidationError,
  isApiErrorBody,
  isRestApiFailureResponse,
  normalizeApiErrorBody,
  resolveApiErrorMessage,
  resolveHttpErrorMessage,
  resolveRestApiFailureHttpStatus,
  shouldSkipCentralizedErrorToast
} from '../utils/api-error-message.util';
import { isMaintenanceRoute, writeMaintenanceNavigationState } from '../utils/maintenance-navigation-state.util';

const isHttpErrorResponse = (error: unknown): error is HttpErrorResponse => error instanceof HttpErrorResponse;

const persistMaintenanceNavigationState = (currentUrl: string): void => {
  writeMaintenanceNavigationState({ preUrl: currentUrl });
};

const isNetworkOrGatewayFailure = (error: HttpErrorResponse): boolean => {
  return error.status === HTTP_STATUS.UNKNOWN && error.error instanceof ProgressEvent && navigator.onLine;
};

const shouldRedirectToMaintenancePage = (error: HttpErrorResponse, currentUrl: string): boolean => {
  const isMaintenanceFailure = MAINTENANCE_ERROR_STATUSES.has(error.status) || isNetworkOrGatewayFailure(error);
  return isMaintenanceFailure && !isMaintenanceRoute(currentUrl);
};

const shouldShowErrorToast = (statusCode: number): boolean => {
  return statusCode !== HTTP_STATUS.UNKNOWN && !MAINTENANCE_ERROR_STATUSES.has(statusCode);
};

const showCentralizedErrorToast = (
  toaster: IToasterService,
  body: unknown,
  request: Parameters<HttpInterceptorFn>[0],
  httpStatus: number
): void => {
  forEachApiErrorBody(body, errorBody => {
    if (shouldSkipCentralizedErrorToast(errorBody, request)) {
      return;
    }
    toaster.error(resolveApiErrorMessage(errorBody, httpStatus));
  });
};

const toHttpErrorFromOkResponse = (response: HttpResponse<unknown>): HttpErrorResponse => {
  const body = normalizeApiErrorBody(response.body);

  return new HttpErrorResponse({
    error: body,
    status: resolveRestApiFailureHttpStatus(response.status),
    statusText: response.statusText,
    url: response.url ?? undefined
  });
};

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const toaster = inject<IToasterService>(TOASTER_SERVICE);
  const apiHealthCheckService = inject(ApiHealthCheckService);

  return next(request).pipe(
    map(event => {
      if (!(event instanceof HttpResponse) || event.body === null) {
        return event;
      }
      const body = normalizeApiErrorBody(event.body);
      if (isRestApiFailureResponse(body) || hasGraphQLMutationValidationError(body)) {
        throw toHttpErrorFromOkResponse(event);
      }
      if (hasAnyApiErrorInPayload(body)) {
        showCentralizedErrorToast(toaster, body, request, event.status);
      }
      return event;
    }),
    catchError((err: unknown) => {
      if (!isHttpErrorResponse(err)) {
        return throwError(() => err);
      }

      const httpError = err;
      const status = httpError.status ?? HTTP_STATUS.UNKNOWN;
      const hasKnownApiError = isApiErrorBody(normalizeApiErrorBody(httpError.error));

      const exceptionCode = (httpError.error as { exception_code?: string } | null)?.exception_code;
      const errorMessage = (httpError.error as { message?: string } | null)?.message;
      const isAuthError =
        status === HttpStatusCode.UNAUTHORIZED ||
        exceptionCode === ERROR_EXCEPTION_CODES.INVALID_OR_EXPIRED_TOKEN ||
        (status === HttpStatusCode.FORBIDDEN && errorMessage === INVALID_API_TOKEN_ERROR_MESSAGE);

      if (isAuthError || shouldSkipCentralizedErrorToast(httpError, request)) {
        return throwError(() => httpError);
      }

      if (shouldRedirectToMaintenancePage(httpError, router.url)) {
        return apiHealthCheckService.checkHealth().pipe(
          switchMap(isHealthy => {
            if (isHealthy) {
              toaster.error(resolveHttpErrorMessage(httpError));
              return throwError(() => httpError);
            }

            if (hasKnownApiError) {
              toaster.error(resolveHttpErrorMessage(httpError));
            }

            persistMaintenanceNavigationState(router.url);
            router.navigateByUrl('/maintenance', { replaceUrl: true });
            return throwError(() => httpError);
          })
        );
      }

      if (shouldShowErrorToast(status) || hasKnownApiError) {
        toaster.error(resolveHttpErrorMessage(httpError));
      }

      if (status === HTTP_STATUS.UNKNOWN && httpError.error instanceof ProgressEvent) {
        toaster.error(SERVER_ERROR_MESSAGES[HttpStatusCode.SERVICE_UNAVAILABLE]);
      }

      return throwError(() => httpError);
    })
  );
};
